import { makeAutoObservable } from 'mobx'

import { getStoreCreator } from '@/utils/mobx/getStoreCreator'

/**
 * The phone's "How it works" rail: which step is under the reader's thumb, and whether the rail is on
 * screen at all. Nothing else is state here — the steps themselves are howSteps.ts.
 *
 * `inView` exists for one card: step 01's miniature is a loop of two screens (StepVisualProduct) and
 * it must start from the first screen when the reader arrives, not be caught halfway. On the desktop
 * the row's reveal decides that; here it is "the rail is on screen and this is the slide in front".
 */
export class HowRailStore {
  active = 0
  inView = false
  readonly count: number

  constructor({ count }: { count: number }) {
    this.count = count
    makeAutoObservable(this, { count: false })
  }

  setInView = (inView: boolean) => {
    this.inView = inView
  }

  setActive = (index: number) => {
    this.active = index
  }

  isPlaying = (index: number) => this.inView && this.active === index

  /**
   * The rail's element, observed while it is mounted. Returns the disposer.
   *
   * The active slide is read off `scrollLeft`, not an IntersectionObserver per slide: with snap every
   * resting position is a whole slide, and between two of them the dot should follow the thumb, which
   * a threshold crossing reports late or twice. One rAF per frame of scrolling, passive.
   */
  attach = (rail: HTMLElement): (() => void) => {
    let frame = 0
    const read = () => {
      frame = 0
      this.setActive(slideAt(rail.scrollLeft, slideStep(rail), this.count))
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read)
    }
    rail.addEventListener('scroll', onScroll, { passive: true })

    /* No observer (an old browser, a test): the rail counts as on screen, so step 01 still plays. */
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(([entry]) => this.setInView(Boolean(entry?.isIntersecting)), { threshold: 0.35 })
    if (observer) observer.observe(rail)
    else this.setInView(true)

    return () => {
      rail.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
      observer?.disconnect()
    }
  }
}

export const useHowRailStore = getStoreCreator(HowRailStore)

/** The distance from one slide's start to the next: its width plus the rail's gap. */
export const slideStep = (rail: HTMLElement): number => {
  const [first, second] = Array.from(rail.children) as HTMLElement[]
  if (!first) return 0
  return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth
}

/** Which slide a scroll offset rests on, clamped to the list. */
export const slideAt = (scrollLeft: number, step: number, count: number): number => {
  if (step <= 0 || count <= 0) return 0
  return Math.min(count - 1, Math.max(0, Math.round(scrollLeft / step)))
}

/**
 * Bring slide `index` to the front, the way a swipe would. Instant for a reader who asked the page not
 * to move: a smooth scroll is exactly the motion `prefers-reduced-motion` turns off.
 */
export const scrollToSlide = (rail: HTMLElement, index: number): void => {
  const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  rail.scrollTo({ left: index * slideStep(rail), behavior: reduce ? 'auto' : 'smooth' })
}
